import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpBackend,
    HttpClient,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, tap, finalize } from 'rxjs/operators';
import { ApiService, AuthService, AuthToken } from '@solcre-org/core';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment.local';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
    private authService = inject(AuthService);
    private apiService = inject(ApiService);
    private router = inject(Router);
    private httpBackend = inject(HttpBackend);
    private httpClient: HttpClient;

    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<string | null> =
        new BehaviorSubject<string | null>(null);

    constructor() {
        this.httpClient = new HttpClient(this.httpBackend);
    }

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        // Only add token for requests to our API, not external URLs (e.g., S3 pre-signed URLs)
        if (this.isApiRequest(request.url)) {
            const token = this.authService.getAccessToken();
            if (token && token.token) {
                request = this.addToken(request, token.token);
            }
        }

        return next.handle(request).pipe(
            catchError((error) => {
                if (error instanceof HttpErrorResponse && error.status === 401) {
                    // Only handle 401 for API requests
                    if (!this.isApiRequest(request.url)) {
                        return throwError(() => error);
                    }
                    if (request.url.includes(environment.API.OAUTH_REFRESH_URI)) {
                        this.handleRefreshTokenFail();
                        return throwError(() => error);
                    }
                    return this.handle401Error(request, next);
                } else {
                    return throwError(() => error);
                }
            })
        );
    }

    private isApiRequest(url: string): boolean {
        return url.startsWith(environment.API_URL) || url.startsWith('/api');
    }

    private addToken(request: HttpRequest<any>, token: string) {
        return request.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`,
            },
        });
    }

    private handle401Error(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            const refreshUrl = `${environment.API_URL}${environment.API.OAUTH_REFRESH_URI}`;

            const currentToken = this.authService.getAccessToken();
            const refreshToken = (currentToken as any)?.refreshToken || (currentToken as any)?.refresh_token;

            if (!refreshToken) {
                this.handleRefreshTokenFail();
                return throwError(() => new Error('No refresh token available'));
            }

            const payload = { refresh_token: refreshToken };

            return this.httpClient.post<any>(refreshUrl, payload).pipe(
                tap((response: any) => {
                    const newToken = response.token;

                    if (newToken) {
                        // Persist through AuthService so its in-memory token stays in sync with localStorage.
                        this.authService.saveToken(response);
                        this.apiService.setAccessToken(newToken);

                        // Notify the subject
                        this.refreshTokenSubject.next(newToken);

                        // Keep legacy listeners in sync with the refreshed token.
                        this.authService.onAccessTokenRefreshed.emit(this.authService.getAccessToken());
                    }
                }),
                catchError((err) => {
                    this.handleRefreshTokenFail();
                    return throwError(() => err);
                }),
                finalize(() => {
                    this.isRefreshing = false;
                }),
                switchMap((response: any) => {
                    // Retry with new token
                    const token = this.authService.getAccessToken()?.token ?? response?.token;
                    if (!token) {
                        return throwError(() => new Error('No refreshed token available'));
                    }
                    return next.handle(this.addToken(request, token));
                })
            );
        } else {
            return this.refreshTokenSubject.pipe(
                filter((token) => token != null),
                take(1),
                switchMap((jwt) => {
                    return next.handle(this.addToken(request, jwt!));
                })
            );
        }
    }

    private handleRefreshTokenFail() {
        localStorage.removeItem('kma-ui-token');
        this.authService.logout();
        this.router.navigate([environment.STATES.LOGIN]);
    }
}
